import { useState, useEffect, useMemo, useRef } from "react";
import EmptyState from "@/components/EmptyState";
import Table, { TableControls, TableFooter } from "@/components/Table";
import { useQuery } from "@apollo/client";
import {
  GET_PDR_HISTORY_QUERY,
  GET_PDR_REQUIREMENTS_QUERY,
  GET_PDR_REQUIREMENT_CYCLE_OPTIONS_QUERY,
  GET_PDR_ACTIVITY_TYPES_QUERY,
  type EligibleActivityTypesQueryResponse,
  type PdrHistoryQueryResponse
} from "../queries";
import { useLocation, useParams } from "react-router-dom";
import { toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { PDRHistory, CycleStatus, Person, PdrGateObligation } from "@/types";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import DonutChart from "@/components/Chart/DonutChart";
import CardSkeleton from "@/components/Skeleton/CardSkeleton";
import { get, snakeCase } from "lodash";
import { CycleRequirements } from "@/types";
import SkeletonList from "@/components/Skeleton/SkeletonList";
import { Path } from "@/router";
import TabLayout from "../_TabLayout";
import { usePersonEducationAccess } from "../../person/_usePersonEducationAccess";
import { Controller } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import MultiSelect from "@/components/MultiSelect";
import { useSearch } from "@/hooks/useSearch";
import { ColumnSort } from "@tanstack/react-table";
import { EditParticipationModal } from "@/features/participation";
import AdjustRequirementsModal from "./_components/AdjustRequirementsModal";
import Select from "@/components/Select";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import IconButton from "@/components/IconButton";
import { CellContext } from "@tanstack/react-table";
import { DeleteParticipationModal } from "@/features/participation";
import { useHasCapabilityBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { filterByClientSearch, type ClientSearchValue } from "@/lib/clientSearch";
import { useHasRoles } from "@/hooks/useCapabilities";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import SectionHeading from "@/components/SectionHeading";
import { useParams as useSearchParams, useSearchWithParams } from "@/hooks/useSearchParams";
import pluralize from "@/lib/pluralize";
import usePagination from "@/hooks/usePagination";
import { useFlags } from "@/hooks/useFeatureFlags";
import useCapabilities from "@/hooks/useCapabilities";
import { getCanAccessActivityManagement } from "@/hooks/useActivityTypePermissions";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import AddTypeLevelParticipationModal from "@/pages/activities/activity-types/[activityTypeId]/_components/AddTypeLevelParticipationModal";
import useModal from "@/hooks/useModal";
import Button from "@/components/Button";
import DateSelect from "@/components/DateSelect";
import type { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import { PERSON_DETAILS_HEADER_QUERY } from "@/pages/people/[pgaId]/queries";
import { buildParticipationActions } from "@/pages/activities/activity-types/[activityTypeId]/participationUtils";
import { isCcmsLegacyPdrCredit, isTypeLevelPdrParticipation } from "@/features/participation";
import useAmplitude from "@/hooks/useAmplitude";
import {
  PDR_ANALYTICS_EVENTS,
  getCycleHistoryFilteredAnalyticsData,
  getCycleViewedAnalyticsData,
  getAddParticipationFlowStartedAnalyticsData
} from "@/features/participation/_utils/amplitude";
import Banner from "@/components/Banner";
import {
  getGateEligibilityLabel,
  getGateEligibilitySubtext,
  getGateStatusLabel,
  isGateRequirementsView,
  resolveGateCohortCode,
  resolveGateSatisfied
} from "./pdrGateHelpers";
import { buildGateCalculationTooltip, findGateCalculationBucket } from "./pdrGateCalculationTooltip";

type PdrHistoryData = PdrHistoryQueryResponse;

type PdrRequirementsData = {
  pdrRequirements: {
    cycleRequirements: CycleRequirements;
    cycleRequirementHistory?: CycleRequirements[];
    gateObligation?: PdrGateObligation | null;
  };
};

type PdrRequirementCycleOption = {
  cycleCode: string;
  startsOn?: string | null;
  endsOn?: string | null;
  requirementsEditable: boolean;
};

type PdrRequirementCycleOptionsData = {
  pdrRequirementCycleOptions: PdrRequirementCycleOption[];
};

type PersonHeaderData = {
  person: Person;
};

type EligibleActivityType = EligibleActivityTypesQueryResponse["activityTypes"]["edges"][number]["node"];

export const DEFAULT_PDR_CYCLE_CODE = "18";

/** Cycles whose earned credits are stored as CE/SP on `cred_unit`, not ED/MT/MS activity category. */
export const CE_SP_PDR_CYCLE_CODES = new Set(["7", "8", "9", "10", "11"]);

const CE_SP_REQUIREMENT_TYPE_OPTIONS = [
  { value: "CE", label: "Continuing Education" },
  { value: "SP", label: "Service Points" }
];

const CATEGORY_REQUIREMENT_TYPE_OPTIONS = [
  { value: "ED", label: "Education/Player Engagement" },
  { value: "MS", label: "Total PDRs" },
  { value: "MT", label: "Meetings/Surveys" }
];

function formatCycleOptionLabel(option: PdrRequirementCycleOption): string {
  if (!option.startsOn || !option.endsOn) {
    return option.cycleCode;
  }

  const start = DateTime.fromISO(option.startsOn).toFormat("MMM yyyy");
  const end = DateTime.fromISO(option.endsOn).toFormat("MMM yyyy");
  return `${option.cycleCode} (${start} - ${end})`;
}

const emptyActivityDateRange: DateValueType = { startDate: null, endDate: null };

function activityDateValueToIso(date: DateValueType, field: "startDate" | "endDate"): string | undefined {
  if (!date) return undefined;
  const val = date[field];
  if (!val) return undefined;
  if (val instanceof Date) return DateTime.fromJSDate(val).toISODate() ?? undefined;
  if (typeof val === "string") {
    const dt = DateTime.fromISO(val, { zone: "utc" });
    return dt.isValid ? (dt.toISODate() ?? undefined) : undefined;
  }
  return undefined;
}

function hasActivityDateRange(dateRange: DateValueType): boolean {
  return (
    activityDateValueToIso(dateRange, "startDate") !== undefined ||
    activityDateValueToIso(dateRange, "endDate") !== undefined
  );
}

function formatPdrCreditTotal(value: number): string {
  return Number(value).toString();
}

const formatTableData = (data: Array<PDRHistory | null | undefined>) => {
  return data.flatMap((item) => {
    if (item == null) return [];

    return [
      {
        activityParticipation: item.activityParticipation,
        id: item.id,
        creditType: item.creditType,
        category: item.category,
        description: item.description,
        submittedAt: item.submittedAt,
        activityDate: item.startedOn,
        creditsAccepted: item.creditsAccepted,
        activityCode: item.activityCode
      }
    ];
  });
};

const RequirementsLoading = () => {
  return (
    <>
      <div className="py-8 w-[500px]">
        <SkeletonList rows={2} />
      </div>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-12">
        {Array.from({ length: 3 }).map((_, index) => (
          <CardSkeleton key={index} rows={5} width={100} />
        ))}
      </div>
    </>
  );
};

function SelectActivityTypeParticipationModal({
  activityTypes,
  loading,
  onDismiss,
  onSelect
}: {
  activityTypes: EligibleActivityType[];
  loading: boolean;
  onDismiss: () => void;
  onSelect: (activityType: EligibleActivityType) => void;
}) {
  const { Modal } = useModal();
  const [selectedActivityTypeId, setSelectedActivityTypeId] = useState("");

  const options = useMemo(
    () =>
      activityTypes.map((activityType) => ({
        value: activityType.id,
        label: activityType.title || "Untitled activity type"
      })),
    [activityTypes]
  );

  const selectedActivityType = activityTypes.find((activityType) => activityType.id === selectedActivityTypeId);

  return (
    <Modal
      onClosed={onDismiss}
      headline="Select Activity Type"
      displayOverflow
      action={
        <Button
          disabled={!selectedActivityType || loading}
          onClick={() => {
            if (selectedActivityType) {
              onSelect(selectedActivityType);
            }
          }}
        >
          Continue
        </Button>
      }
      className="sm:max-w-[480px]"
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-brand-on-surface-variant">
          Choose the activity type for this participation.
        </p>

        {loading ? (
          <p className="text-body-medium">Loading eligible activity types...</p>
        ) : activityTypes.length === 0 ? (
          <p className="text-body-medium">No eligible activity types are available for direct participation.</p>
        ) : (
          <Select
            label="Activity Type"
            placeholder="Select an activity type"
            options={options}
            value={selectedActivityTypeId}
            onSelect={setSelectedActivityTypeId}
            supportingText="Only activity types configured for direct participation are shown."
          />
        )}
      </div>
    </Modal>
  );
}

type RequirementsTableRow = {
  id: string;
  requirementsEffectiveDate: string | null;
  requirementsEndDate: string;
  cohort: string;
  modifiedBy: string;
} & Record<string, ClientSearchValue>;

function formatModifiedBy(author: string | null | undefined) {
  return author || "—";
}

function buildRequirementsTableRow(row: CycleRequirements, rowIndex: number): RequirementsTableRow {
  const requirementValues = (row.requirements ?? []).reduce<Record<string, ClientSearchValue>>((acc, requirement) => {
    acc[snakeCase(requirement.label)] = requirement.cycleRequirement;
    acc[`${snakeCase(requirement.label)}_deficit`] = requirement.deficit;
    return acc;
  }, {});

  return {
    id: `${row.cycleCode || ""}-${row.requirementsEffectiveDate || ""}-${rowIndex}`,
    requirementsEffectiveDate: toFormat(row.requirementsEffectiveDate || ""),
    requirementsEndDate: (row.requirementsEndDate && toFormat(row.requirementsEndDate)) || "—",
    cohort: row.cohort,
    ...requirementValues,
    modifiedBy: formatModifiedBy(row.modifiedBy)
  };
}

function buildRequirementsTableData(
  cycleRequirements: CycleRequirements | undefined,
  cycleRequirementHistory?: CycleRequirements[]
) {
  const historyRows = cycleRequirementHistory?.length ? cycleRequirementHistory : [];
  const sourceRows = historyRows.length > 0 ? historyRows : cycleRequirements ? [cycleRequirements] : [];

  return sourceRows.map((row, index) => buildRequirementsTableRow(row, index));
}

function requirementsSearchValues(
  row: RequirementsTableRow,
  displayedRequirements: Array<{ label: string; deficit: number }> | undefined,
  isGateView: boolean
): ClientSearchValue[] {
  const values: ClientSearchValue[] = [
    row.requirementsEffectiveDate,
    row.requirementsEndDate,
    row.cohort,
    row.modifiedBy
  ];

  for (const requirement of displayedRequirements ?? []) {
    const key = snakeCase(requirement.label);
    values.push(row[key]);
    if (!isGateView && requirement.deficit > 0) {
      values.push(row[`${key}_deficit`]);
    }
  }

  return values;
}

export const Requirements = ({
  loading,
  cycleRequirements,
  cycleRequirementHistory,
  gateObligation,
  showChangeHistory,
  requirementsEditable = false
}: {
  loading: boolean;
  cycleRequirements: CycleRequirements | undefined;
  cycleRequirementHistory?: CycleRequirements[];
  gateObligation?: PdrGateObligation | null;
  showChangeHistory?: boolean;
  requirementsEditable?: boolean;
}) => {
  const canAdjustRequirements =
    useHasRoles([ROLE.GLOBAL_ADMIN, ROLE.MEMBERSHIP_SERVICES_STAFF, ROLE.MEMBERSHIP_SERVICES_ADMIN]) &&
    requirementsEditable;
  const [adjustRequirementsModalOpen, setAdjustRequirementsModalOpen] = useState(false);
  const { cycleCode, cycleStartsOn, cycleEndsOn, status, requirements, cohort, cohortCode, requirementsEffectiveDate } =
    cycleRequirements || {};

  const gateCohortCode = resolveGateCohortCode(cohortCode, gateObligation);
  const isGateView = isGateRequirementsView(cohortCode, gateObligation);
  const gateSatisfied = resolveGateSatisfied({ status, gateObligation });

  const tableData = useMemo(
    () => buildRequirementsTableData(cycleRequirements, cycleRequirementHistory),
    [cycleRequirements, cycleRequirementHistory]
  );
  const {
    searchValue: requirementsSearch,
    setSearchValue: setRequirementsSearch,
    clearSearch: clearRequirementsSearch
  } = useSearchWithParams({ paramName: "requirementsSearch" });
  const hasSearch = !!requirementsSearch.trim();
  const changeHistoryRequirements = cycleRequirements?.requirements;
  const visibleRows = useMemo(
    () =>
      filterByClientSearch(tableData, requirementsSearch, (row) =>
        requirementsSearchValues(row, changeHistoryRequirements, isGateView)
      ),
    [changeHistoryRequirements, isGateView, requirementsSearch, tableData]
  );

  if (loading) {
    return <RequirementsLoading />;
  }

  const statusMap = {
    [CycleStatus.NO_REQUIREMENTS]: "No Requirements",
    [CycleStatus.REQUIREMENTS_NOT_MET]: "Requirements Not Met",
    [CycleStatus.REQUIREMENTS_MET]: "Requirements Met",
    [CycleStatus.RECOVERING]: "Recovering"
  };

  const statusLabel =
    isGateView && gateCohortCode ? getGateStatusLabel(gateCohortCode, gateSatisfied) : statusMap[status as CycleStatus];

  return (
    <>
      {cycleRequirements ? (
        <>
          <SectionHeading
            heading="Professional Development Requirements (PDR) Status"
            body={`Cycle: ${cycleCode} (${toFormat(cycleStartsOn || "")} - ${toFormat(cycleEndsOn || "")})
          Status: ${statusLabel}
          Cohort: ${cohort}
          Effective Date: ${toFormat(requirementsEffectiveDate || "")}`}
          />
          {isGateView && gateCohortCode ? (
            <div className="mt-6">
              <Banner
                variant={gateSatisfied ? "info" : "warn"}
                description={getGateEligibilityLabel(gateCohortCode)}
                subText={getGateEligibilitySubtext(gateCohortCode, gateSatisfied)}
                inline
                closable={false}
                hideSecondaryDismiss
              />
            </div>
          ) : null}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-12 mt-6">
            {requirements
              ?.slice()
              .sort((a, b) => (a.label === "Total PDRs" ? 1 : b.label === "Total PDRs" ? -1 : 0))
              .map((requirement, index) => {
                const {
                  recoveryRemaining,
                  recoveryThreshold,
                  deficit,
                  earned,
                  totalRequirement,
                  label,
                  stipulationCode
                } = requirement;
                const creditRemaining = totalRequirement - earned;
                const gateCalculationBucket = findGateCalculationBucket(gateObligation?.calculation, stipulationCode);
                const gateCalculationTooltip =
                  isGateView && gateCohortCode && gateCalculationBucket && gateObligation?.calculation
                    ? buildGateCalculationTooltip(gateCalculationBucket, gateObligation.calculation, gateCohortCode)
                    : undefined;
                const chartData = isGateView
                  ? [{ name: "Earned", value: requirement.earned }]
                  : [
                      { name: "Earned", value: requirement.earned },
                      ...(recoveryRemaining > 0 ? [{ name: "Recovery Remaining ", value: recoveryRemaining }] : []),
                      ...(recoveryThreshold > 0
                        ? [{ name: "Recovery Threshold", value: recoveryThreshold, fixedSegment: true }]
                        : [])
                    ];

                // Construct description text (only show if totalRequirement > 0)
                const description =
                  totalRequirement > 0 && creditRemaining > 0
                    ? `${creditRemaining} ${pluralize(creditRemaining, "Credit")} Remaining`
                    : undefined;
                const descriptionTwo =
                  !isGateView &&
                  totalRequirement > 0 &&
                  deficit > 0 &&
                  typeof recoveryThreshold === "number" &&
                  typeof recoveryRemaining === "number"
                    ? earned >= recoveryThreshold
                      ? `Recovered at ${recoveryThreshold} ${pluralize(recoveryThreshold, "Credit")} `
                      : `${recoveryRemaining} of ${recoveryThreshold} ${pluralize(
                          recoveryThreshold,
                          "Credit"
                        )} Remaining to Recover`
                    : undefined;

                return (
                  <DonutChart
                    key={index}
                    data={chartData}
                    total={totalRequirement}
                    title="PDR Status"
                    label={label}
                    displayValue={`${earned} of ${totalRequirement} earned`}
                    description={description}
                    descriptionTwo={descriptionTwo}
                    infoTooltip={
                      gateCalculationTooltip ??
                      (!isGateView && deficit > 0
                        ? {
                            title: "Deficit PDRs",
                            supportingText: `Member was ${deficit} credits short of meeting ${label} requirements from the previous PDR cycle. Member must earn ${deficit}x2=${
                              deficit * 2
                            } credits to reach the recovery threshold in this category. Once the recovery threshold is reached in all categories, the member can be moved out of Class F and continue working towards the current cycle requirements`
                          }
                        : undefined)
                    }
                    successTooltip={
                      earned >= totalRequirement
                        ? {
                            supportingText: `${label} requirements met`
                          }
                        : undefined
                    }
                  />
                );
              })}
          </div>
        </>
      ) : null}
      {showChangeHistory && (
        <>
          <div className="pt-12" />
          <Table
            data={visibleRows}
            loading={loading}
            hasActiveFilters={hasSearch}
            renderNoDataEmptyState={() => (
              <EmptyState
                title="No PDR Requirements."
                caption="This person does not have PDR requirements in this cycle."
                iconName="school"
              />
            )}
            renderFilteredEmptyState={() => (
              <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
            )}
            renderControls={(_table, { onSortClick } = {}) => (
              <TableControls
                actions={
                  canAdjustRequirements
                    ? [
                        {
                          text: cycleRequirements ? "Adjust Requirements" : "Add Requirements",
                          iconName: "add",
                          variant: "outlined",
                          onClick: () => setAdjustRequirementsModalOpen?.(true)
                        }
                      ]
                    : []
                }
                onSortClick={onSortClick}
              >
                <SearchInput
                  placeholder="Search PDR Requirements"
                  query={requirementsSearch}
                  onChange={setRequirementsSearch}
                  onClear={clearRequirementsSearch}
                  pageSearchShortcut={false}
                />
              </TableControls>
            )}
            columns={[
              {
                header: "Effective Date",
                accessorKey: "requirementsEffectiveDate",
                enableSorting: false,
                size: 40
              },
              {
                header: "End Date",
                accessorKey: "requirementsEndDate",
                enableSorting: false,
                size: 40
              },
              {
                header: "Cohort",
                accessorKey: "cohort",
                enableSorting: false,
                size: 40
              },
              ...(changeHistoryRequirements?.flatMap((requirement) => {
                return [
                  {
                    header: `${requirement.label} ${requirement.label.includes("Total") ? "Credits" : ""}`,
                    accessorKey: snakeCase(requirement.label),
                    enableSorting: false,
                    size: 40,
                    meta: {
                      headerClassName: "whitespace-normal"
                    }
                  },
                  ...(!isGateView && requirement.deficit > 0
                    ? [
                        {
                          header: `${requirement.label} Deficit ${
                            requirement.label.includes("Total") ? "Credits" : ""
                          }`,
                          accessorKey: `${snakeCase(requirement.label)}_deficit`,
                          enableSorting: false,
                          size: 40,
                          meta: {
                            headerClassName: "whitespace-normal"
                          }
                        }
                      ]
                    : [])
                ];
              }) ?? []),
              {
                header: "Modified By",
                accessorKey: "modifiedBy",
                enableSorting: false,
                size: 40
              }
            ]}
          />
        </>
      )}
      {adjustRequirementsModalOpen && (
        <AdjustRequirementsModal
          onDismiss={() => setAdjustRequirementsModalOpen(false)}
          cycleRequirements={cycleRequirements}
        />
      )}
    </>
  );
};

export default function ProfessionalDevelopment() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();
  const skipEducationQueries = !loaded || !canViewPersonEducation || !pgaId;
  const location = useLocation();
  const flags = useFlags();
  const capabilities = useCapabilities();
  const { primarySectionId } = usePrimarySection();
  const { searchValue, control, setValue: setSearchValue } = useSearch("name");
  const [selectedTypeValues, setSelectedTypeValues] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([{ id: "submittedAt", desc: true }]);
  const { Menu, setMenu } = useTableRowMenu();
  const { createHeaderPagination, createFooterPagination, resetPagination, paginationVariables } = usePagination();
  const canManageParticipations = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageParticipations
  });
  const canManageLegacyPdrCredits = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageLegacyPdrCredits
  });
  const { params, addSearchParam } = useSearchParams();
  const [selectActivityTypeModalOpen, setSelectActivityTypeModalOpen] = useState(false);
  const [editParticipationModalOpen, setEditParticipationModalOpen] = useState(false);
  const [deleteParticipationModalOpen, setDeleteParticipationModalOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<EligibleActivityType | null>(null);
  const [cycleCode, setCycleCode] = useState(params.cycle || DEFAULT_PDR_CYCLE_CODE);
  const [selectedCycle, setSelectedCycle] = useState<string>(cycleCode);
  const [selectedParticipation, setSelectedParticipation] = useState<any>(null);
  const [activityDateRange, setActivityDateRange] = useState<DateValueType>(emptyActivityDateRange);
  const { trackEvent } = useAmplitude();
  const [participationFlowStartedAtMs, setParticipationFlowStartedAtMs] = useState<number | null>(null);
  const hasTrackedInitialCycleViewRef = useRef(false);

  const showFullTranscriptHistory = selectedCycle === "ALL";
  const activityDateFrom = showFullTranscriptHistory
    ? activityDateValueToIso(activityDateRange, "startDate")
    : undefined;
  const activityDateTo = showFullTranscriptHistory ? activityDateValueToIso(activityDateRange, "endDate") : undefined;
  const activityDateFilterActive = showFullTranscriptHistory && hasActivityDateRange(activityDateRange);

  const {
    data,
    loading,
    error: pdrHistoryError,
    refetch: refetchPdrHistory
  } = useQuery<PdrHistoryData>(GET_PDR_HISTORY_QUERY, {
    errorPolicy: "all",
    skip: skipEducationQueries,
    variables: {
      pgaId,
      search: searchValue,
      cycleCode: selectedCycle === "ALL" ? null : selectedCycle,
      creditTypes: selectedTypeValues,
      fullTranscript: showFullTranscriptHistory,
      activityDateFrom,
      activityDateTo,
      ...(orderBy[0] && {
        orderBy: {
          field: snakeCase(orderBy[0].id).toUpperCase(),
          direction: orderBy[0].desc ? "DESC" : "ASC"
        }
      }),
      ...paginationVariables
    }
  });
  const {
    data: requirementsData,
    loading: requirementsLoading,
    refetch: refetchPdrRequirements
  } = useQuery<PdrRequirementsData>(GET_PDR_REQUIREMENTS_QUERY, {
    variables: { pgaId, cycleCode },
    skip: skipEducationQueries
  });
  const { data: cycleOptionsData, loading: cycleOptionsLoading } = useQuery<PdrRequirementCycleOptionsData>(
    GET_PDR_REQUIREMENT_CYCLE_OPTIONS_QUERY,
    { skip: skipEducationQueries }
  );
  const {
    data: activityTypesData,
    loading: activityTypesLoading,
    refetch: refetchActivityTypes
  } = useQuery<EligibleActivityTypesQueryResponse>(GET_PDR_ACTIVITY_TYPES_QUERY, {
    variables: {
      first: 250,
      filters: { allowsParticipation: true }
    },
    skip: skipEducationQueries
  });
  const { data: personHeaderData } = useQuery<PersonHeaderData>(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    skip: skipEducationQueries
  });

  const cycleRequirements = get(requirementsData, "pdrRequirements.cycleRequirements", undefined);
  const cycleRequirementHistory = get(requirementsData, "pdrRequirements.cycleRequirementHistory", undefined);
  const gateObligation = get(requirementsData, "pdrRequirements.gateObligation", undefined);
  const requirementsEditable =
    cycleOptionsData?.pdrRequirementCycleOptions?.find((option) => option.cycleCode === cycleCode)
      ?.requirementsEditable ?? false;
  const viewingCycleOptions = useMemo(() => {
    const options = cycleOptionsData?.pdrRequirementCycleOptions ?? [];

    if (options.length > 0) {
      return options.map((option) => ({
        label: formatCycleOptionLabel(option),
        value: option.cycleCode
      }));
    }

    return [
      { label: "18 (Jun 2025 - Jun 2028)", value: "18" },
      { label: "17 (Jun 2022 - Jun 2025)", value: "17" }
    ];
  }, [cycleOptionsData?.pdrRequirementCycleOptions]);

  const memberCurrentCycleCode = useMemo(() => {
    const codes = cycleOptionsData?.pdrRequirementCycleOptions?.map((option) => option.cycleCode) ?? [];
    if (!codes.length) return DEFAULT_PDR_CYCLE_CODE;
    return codes.sort((a, b) => Number(b) - Number(a))[0];
  }, [cycleOptionsData?.pdrRequirementCycleOptions]);

  const trackCycleViewed = (viewingCycleCode: string) => {
    trackEvent(
      PDR_ANALYTICS_EVENTS.cycleViewed,
      getCycleViewedAnalyticsData({
        viewingCycleCode,
        memberCurrentCycleCode,
        isNonCurrentMemberCycle: viewingCycleCode !== memberCurrentCycleCode
      })
    );
  };

  useEffect(() => {
    if (cycleOptionsLoading || hasTrackedInitialCycleViewRef.current) return;

    hasTrackedInitialCycleViewRef.current = true;
    trackEvent(
      PDR_ANALYTICS_EVENTS.cycleViewed,
      getCycleViewedAnalyticsData({
        viewingCycleCode: cycleCode,
        memberCurrentCycleCode,
        isNonCurrentMemberCycle: cycleCode !== memberCurrentCycleCode
      })
    );
  }, [cycleOptionsLoading, cycleCode, memberCurrentCycleCode, trackEvent]);

  useEffect(() => {
    setSelectedTypeValues([]);
  }, [cycleCode]);

  const tableData = formatTableData(data?.pdrHistoryPaginated?.nodes || []);
  const person = personHeaderData?.person;
  const eligibleActivityTypes = useMemo(
    () =>
      (activityTypesData?.activityTypes.edges || [])
        .map((edge) => edge.node)
        .filter((activityType) => (activityType.config?.creditRates?.length ?? 0) > 0)
        .filter((activityType) => getCanAccessActivityManagement(activityType, capabilities, primarySectionId)),
    [activityTypesData?.activityTypes.edges, capabilities, primarySectionId]
  );
  const canOpenTypeLevelParticipationFlow = activityTypesLoading || eligibleActivityTypes.length > 0;
  const showAddParticipationButton = canOpenTypeLevelParticipationFlow || canManageLegacyPdrCredits;

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const requirementTypeOptions = useMemo(() => {
    // All History is not a single late-PDP cycle, so keep later-era ED/MS/MT
    // even when Viewing Cycle is 7–11. CE/SP only apply to selected 7–11.
    if (CE_SP_PDR_CYCLE_CODES.has(cycleCode) && selectedCycle !== "ALL") {
      return CE_SP_REQUIREMENT_TYPE_OPTIONS;
    }

    return CATEGORY_REQUIREMENT_TYPE_OPTIONS;
  }, [cycleCode, selectedCycle]);

  const cycleOptions = useMemo(() => {
    return [
      { value: cycleCode, label: "Selected Cycle Only" },
      { value: "ALL", label: "Show All Cycle History" }
    ];
  }, [cycleCode]);

  useEffect(() => {
    addSearchParam("cycle", cycleCode);
    if (selectedCycle !== "ALL") {
      setSelectedCycle(cycleCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addSearchParam, cycleCode]);

  const onClickEditParticipation = (participation: PDRHistory) => {
    setEditParticipationModalOpen(true);
    setSelectedParticipation(participation);
  };

  const onClickDeleteParticipation = (participation: PDRHistory) => {
    setDeleteParticipationModalOpen(true);
    setSelectedParticipation(participation);
  };

  const openAddParticipationFlow = async () => {
    const startedAt = Date.now();
    setParticipationFlowStartedAtMs(startedAt);

    trackEvent(
      PDR_ANALYTICS_EVENTS.addParticipationStarted,
      getAddParticipationFlowStartedAnalyticsData({
        source: "person_professional_development",
        participationKind: "type-level"
      })
    );

    await refetchActivityTypes();
    setSelectActivityTypeModalOpen(true);
  };

  const handleTypeLevelParticipationSuccess = async () => {
    await Promise.all([refetchPdrHistory(), refetchPdrRequirements()]);
    setSelectedActivityType(null);
    setSelectActivityTypeModalOpen(false);
    setParticipationFlowStartedAtMs(null);
  };

  const hasFiltersApplied =
    !!(searchValue || "").trim() ||
    selectedTypeValues.length > 0 ||
    selectedCycle === "ALL" ||
    activityDateFilterActive;
  const hasActiveFilters = hasFiltersApplied || (!loading && tableData.length === 0);

  const getPdrHistoryRowLink = (row: { original: (typeof tableData)[number] }) => {
    const activityParticipation = row.original.activityParticipation;
    if (!flags["backoffice-activities-screen"] || isCcmsLegacyPdrCredit(row.original)) {
      return { to: `/people/${pgaId}/education/professional-development/${row.original.id}` as Path };
    }

    const activityTypeId = isTypeLevelPdrParticipation(row.original)
      ? activityParticipation!.activityType?.id
      : activityParticipation!.activity?.activityType.id;

    if (!activityTypeId) {
      return { to: `/people/${pgaId}/education/professional-development/${row.original.id}` as Path };
    }

    const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
    return {
      to: `/activities/activity-types/${activityTypeId}/participants/${activityParticipation!.id}?returnUrl=${returnUrl}` as Path
    };
  };

  if (!loaded) {
    return null;
  }

  return (
    <TabLayout>
      <Menu />
      <div className="flex w-full">
        <div className="w-full mt-12">
          <Select
            onSelect={(v: string) => {
              setCycleCode(v);
              setSelectedTypeValues([]);
              trackCycleViewed(v);
            }}
            options={viewingCycleOptions}
            value={cycleCode}
            label="Viewing Cycle"
            className="max-w-[252px]"
          />

          <Requirements
            loading={requirementsLoading}
            cycleRequirements={cycleRequirements}
            cycleRequirementHistory={cycleRequirementHistory}
            gateObligation={gateObligation}
            showChangeHistory
            requirementsEditable={requirementsEditable}
          />
          <div className="w-full">
            <div className="pt-12" />
            {pdrHistoryError && !loading && tableData.length > 0 && (
              <div role="alert" className="mb-4 text-body-medium text-sys-brand-error">
                Some PDR history records could not be loaded. Showing the records that did load.
              </div>
            )}
            <Table
              data={tableData}
              loading={loading}
              enableRowSelection={false}
              onSortingChange={handleSort}
              sortingState={orderBy}
              serverSideSorting={true}
              getRowLink={getPdrHistoryRowLink}
              renderControls={() => (
                <TableControls
                  actions={
                    showAddParticipationButton
                      ? [
                          {
                            text: "Add Participation",
                            iconName: "add",
                            variant: "text",
                            onClick: openAddParticipationFlow
                          }
                        ]
                      : []
                  }
                  pagination={createHeaderPagination(data?.pdrHistoryPaginated, {
                    totalCount: data?.pdrHistoryPaginated?.totalCount || 0,
                    pageCount: tableData.length
                  })}
                  activeFilters={
                    (selectedTypeValues.length > 0 ? 1 : 0) +
                    (selectedCycle === "ALL" ? 1 : 0) +
                    (activityDateFilterActive ? 1 : 0)
                  }
                  useExpandedFilters={true}
                  expandedFiltersState={{
                    requirementType: selectedTypeValues.length > 0,
                    cycle: selectedCycle === "ALL",
                    activityDateRange: activityDateFilterActive
                  }}
                  onClearAllFilters={() => {
                    setSelectedTypeValues([]);
                    setSelectedCycle(cycleCode);
                    setActivityDateRange(emptyActivityDateRange);
                  }}
                >
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <SearchInput
                        placeholder="Search Activities"
                        query={field.value}
                        onClear={() => {
                          setSearchValue("query", "");
                          resetPagination();
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          resetPagination();
                        }}
                      />
                    )}
                  />
                  <div data-name="requirementType">
                    <Controller
                      name="requirementType"
                      control={control}
                      render={() => (
                        <MultiSelect
                          label="Requirement Type"
                          selectedValues={selectedTypeValues}
                          options={requirementTypeOptions}
                          onSelect={(value, checked) => {
                            const type = checked
                              ? [...selectedTypeValues, value]
                              : selectedTypeValues.filter((v) => v !== value);
                            setSelectedTypeValues(type);
                            resetPagination();
                          }}
                          onClear={() => {
                            setSelectedTypeValues([]);
                            resetPagination();
                          }}
                          className="w-full"
                        />
                      )}
                    />
                  </div>
                  <div data-name="cycle" data-filter-size="custom">
                    <Controller
                      name="cycle"
                      control={control}
                      render={() => (
                        <Select
                          label="Cycle"
                          options={cycleOptions}
                          className="min-w-[250px]"
                          value={selectedCycle}
                          onSelect={(value) => {
                            setSelectedCycle(value);
                            setSelectedTypeValues([]);
                            if (value !== "ALL") {
                              setActivityDateRange(emptyActivityDateRange);
                            }
                            trackEvent(
                              PDR_ANALYTICS_EVENTS.cycleHistoryFiltered,
                              getCycleHistoryFilteredAnalyticsData({
                                historyScope: value === "ALL" ? "all_cycles" : "selected_cycle",
                                viewingCycleCode: cycleCode,
                                hasActivityDateRange: value === "ALL" ? hasActivityDateRange(activityDateRange) : false
                              })
                            );
                            resetPagination();
                          }}
                        />
                      )}
                    />
                  </div>
                  {showFullTranscriptHistory && (
                    <div data-name="activityDateRange" data-filter-size="custom">
                      <DateSelect
                        label="Activity Date Range"
                        value={activityDateRange}
                        onChange={(val) => {
                          setActivityDateRange((val as DateValueType) ?? emptyActivityDateRange);
                          resetPagination();
                        }}
                        placeholder="All dates"
                        supportingText="Optional. Leave blank for the full credit history."
                        useModal
                      />
                    </div>
                  )}
                </TableControls>
              )}
              columns={[
                {
                  header: "Activity Type",
                  accessorKey: "category",
                  enableSorting: true,
                  size: 300,
                  sortingFn: "customAlphanumericSorting"
                },
                {
                  header: "Title",
                  accessorKey: "description",
                  enableSorting: true
                },
                {
                  header: "Submitted",
                  accessorKey: "submittedAt",
                  enableSorting: true,
                  cell: ({ row }) => {
                    return <div>{toUTCFormat(row.original.submittedAt)}</div>;
                  }
                },
                {
                  header: "Activity Date",
                  accessorKey: "startedOn",
                  enableSorting: true,
                  cell: ({ row }) => {
                    return <div>{toFormat(row.original.activityDate)}</div>;
                  }
                },
                {
                  header: "Requirement Type",
                  accessorKey: "creditType",
                  enableSorting: true,
                  meta: {
                    className: getStickyColumnClasses(!!data?.pdrHistoryPaginated)
                  }
                },
                {
                  header: "Credits",
                  accessorKey: "creditsAccepted",
                  enableSorting: true,
                  meta: {
                    headerClassName: "max-sm:inline"
                  }
                },

                {
                  id: "actions",
                  header: "",
                  size: 10,
                  cell: (cellProps: CellContext<any, unknown>) => {
                    const {
                      row: { id: rowId, original }
                    } = cellProps;

                    const actions = buildParticipationActions({
                      canManageParticipations,
                      hasCredit: !!original.id,
                      isTypeLevelParticipation: isTypeLevelPdrParticipation(original),
                      isCcmsLegacyCredit: isCcmsLegacyPdrCredit(original),
                      canManageLegacyPdrCredits,
                      canEdit: original.activityParticipation?.canEdit,
                      canDelete: original.activityParticipation?.canDelete,
                      onEdit: () => onClickEditParticipation(original),
                      onDelete: () => onClickDeleteParticipation(original)
                    });

                    if (!actions.length) return null;

                    function onClick(e: React.MouseEvent) {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenu({ rowId, actions });
                    }

                    return (
                      <div className="flex items-center gap-2 justify-end">
                        <IconButton
                          id={actionId(rowId)}
                          onClick={onClick}
                          data-testid="actions"
                          title="actions"
                          name="more_vert"
                          iconClassName="font-extrabold"
                        />
                      </div>
                    );
                  }
                }
              ]}
              hasActiveFilters={hasActiveFilters}
              renderFilteredEmptyState={() => (
                <EmptyState
                  title={
                    pdrHistoryError && tableData.length === 0
                      ? "Unable to load PDR History"
                      : hasFiltersApplied
                        ? "No Results Found"
                        : "No PDR History"
                  }
                  caption={
                    pdrHistoryError && tableData.length === 0
                      ? "An error occurred while loading PDR history"
                      : hasFiltersApplied
                        ? "Try a different search or filter"
                        : "This person does not have any Professional Development Requirement history"
                  }
                  iconName={
                    pdrHistoryError && tableData.length === 0 ? "error" : hasFiltersApplied ? "search" : "school"
                  }
                />
              )}
              renderFooter={() => {
                const totalCreditsAccepted =
                  activityDateFilterActive && data?.pdrHistoryPaginated?.totalCreditsAccepted != null
                    ? data.pdrHistoryPaginated.totalCreditsAccepted
                    : null;

                return (
                  <div
                    className={`flex flex-col md:flex-row md:items-center ${totalCreditsAccepted != null ? "justify-between" : "justify-end"}`}
                  >
                    {totalCreditsAccepted != null && (
                      <div className="flex items-center gap-2 text-body-medium-semibold mb-4 md:mb-0">
                        <span>Total Credits:</span>
                        <span>{formatPdrCreditTotal(totalCreditsAccepted)}</span>
                      </div>
                    )}
                    <TableFooter
                      pagination={createFooterPagination(data?.pdrHistoryPaginated, {
                        totalCount: data?.pdrHistoryPaginated?.totalCount || 0,
                        pageCount: tableData.length
                      })}
                      totalRowCount={data?.pdrHistoryPaginated?.totalCount}
                    />
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      {selectActivityTypeModalOpen && (
        <SelectActivityTypeParticipationModal
          activityTypes={eligibleActivityTypes}
          loading={activityTypesLoading}
          onDismiss={() => {
            setSelectActivityTypeModalOpen(false);
            setParticipationFlowStartedAtMs(null);
          }}
          onSelect={(activityType) => {
            setSelectedActivityType(activityType);
            setSelectActivityTypeModalOpen(false);
          }}
        />
      )}
      {selectedActivityType && (
        <AddTypeLevelParticipationModal
          onDismiss={() => {
            setSelectedActivityType(null);
            setParticipationFlowStartedAtMs(null);
          }}
          activityTypeId={selectedActivityType.id}
          creditRates={selectedActivityType.config?.creditRates ?? []}
          preselectedPerson={person}
          preselectedPgaId={pgaId || ""}
          entryPoint="person_professional_development"
          participationFlowStartedAtMs={participationFlowStartedAtMs}
          onSuccess={handleTypeLevelParticipationSuccess}
        />
      )}
      {editParticipationModalOpen && (
        <EditParticipationModal
          onDismiss={() => setEditParticipationModalOpen(false)}
          pgaId={pgaId || ""}
          activityId={selectedParticipation?.id}
          activityParticipationId={selectedParticipation?.activityParticipation?.id}
          isTypeLevelParticipation={
            selectedParticipation ? isTypeLevelPdrParticipation(selectedParticipation) : undefined
          }
        />
      )}

      {deleteParticipationModalOpen && (
        <DeleteParticipationModal
          onDismiss={() => setDeleteParticipationModalOpen(false)}
          activityId={selectedParticipation?.id}
          activityParticipationId={selectedParticipation?.activityParticipation?.id}
          isTypeLevelParticipation={
            selectedParticipation ? isTypeLevelPdrParticipation(selectedParticipation) : undefined
          }
        />
      )}
    </TabLayout>
  );
}
